<template>
    <div
        v-observe-visibility="observerOptions"
        :class="rootClasses"
        class="progress-group">

        <div class="progress-group__item"
            :class="getItemClasses(item)"
            v-for="(item, index) in slicedItems"
            @click="onProgressClick(index, item.percent, item.checked)"
            :key="item.code">

            <header class="progress-group__header"
                v-if="withHeader">
                <span class="progress-group__title">
                    {{ item.name }}
                </span>
                <span class="progress-group__percent">
                    {{ item.percent.toFixed(0) }}%
                </span>
            </header>

            <div class="progress-group__main">
                <span class="progress-group__title"
                    v-if="!withHeader">
                    {{ item.name }}
                </span>

                <ProgressBar
                    class="progress-group__progress"
                    :value="item.percent"
                    :deferred-animation="true"
                    :hovered="checkable"
                    :calculate="showValue"
                    :track-color="trackColor"
                    :checked="item.checked"
                    :sensitive="true"
                    :with-value="getWithValue(item.percent)"
                    disabled />

            </div>

            <div class="progress-group__caption"
                v-if="item.caption">
                {{ item.caption }}
            </div>

        </div>
    </div>
</template>

<script>
import includes from 'lodash/includes'
import filter from 'lodash/filter'
import get from 'lodash/get'
import orderBy from 'lodash/orderBy'

import { ObserveVisibility } from 'vue-observe-visibility'
import ProgressBar from '@/components/ui/ProgressBar'

export default {
    name: 'ProgressGroup',
    directives: {
        'observe-visibility': ObserveVisibility
    },
    components: {
        ProgressBar
    },
    props: {
        withValues: {
            type: Boolean,
            default: true
        },
        grid: {
            type: Boolean,
            default: false
        },
        checkable: {
            type: Boolean,
            default: false
        },
        items: {
            type: Array,
            default: () => []
        },
        direction: {
            type: String,
            default: 'horizontal',
            validator(value) {
                return includes(['horizontal', 'vertical'], value)
            }
        },
        sortBy: {
            type: String,
            default: 'percent'
        },
        sortOrder: {
            type: String,
            default: 'desc',
            validator(value) {
                return includes(['desc', 'asc'], value)
            }
        },
        type: {
            type: String,
            default: 'range',
            validator(value) {
                return includes(['range', 'segment'], value)
            }
        },
        trackColor: {
            type: String,
            default: 'violet',
        },
        markerSize: {
            type: Number,
            default: 1
        },
        checked: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: true
        },
        tooltips: {
            type: Array,
            default: () => []
        },
        tooltipTitle: {
            type: String,
            default: ''
        },
        tooltipContent: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            default: ''
        },
        labelMax: {
            type: String,
            default: ''
        },
        legend: {
            type: Array,
            default: () => []
        },
        help: {
            type: String,
            default: ''
        },
        step: {
            type: Number,
            default: 0
        },
        withHeader: {
            type: Boolean,
            default: false
        },
        ordered: {
            type: Boolean,
            default: false
        },
        maxLength: {
            type: [Number, String],
            default: 0
        },
        withEmpties: {
            type: Boolean,
            default: false
        },
        value: {
            type: [Number, String],
            default: 0
        }
    },
    data() {
        return {
            sliderMax: null,
            sliderMin: null,
            sliderValue: null,
            showValue: false,
            observerOptions: {
                callback: this.triggerShowValue,
                throttle: 200,
                once: true,
                throttleOptions: {
                    leading: 'visible',
                },
                intersection: {
                    threshold: 0,
                },
            }
        }
    },
    computed: {
        rootClasses() {
            return {
                'progress-group_checkable': this.checkable,
                'progress-group_grid': this.grid,
            }
        },
        slicedItems() {
            let items = this.items;

            items = !this.withEmpties ?
                filter(this.items, item => {
                    return !includes(
                        [null, undefined, 0, false],
                        get(item, 'percent')
                    )
                }) :
                items

            items = this.ordered ?
                orderBy(items, this.sortBy, this.sortOrder) :
                items;

            return this.maxLength ?
                items.slice(0, this.maxLength) :
                items
        },
        isCheckedState() {
            const checkedItems = filter(this.items, 'checked');
            return checkedItems.length > 0;
        }
    },
    methods: {
        getItemClasses(item) {
            return {
                'progress-group__item_empty': item.total === 0,
                'progress-group__item_with-header': this.withHeader,
                'progress-group__item_checked': item.checked && this.isCheckedState,
                'progress-group__item_unchecked': !item.checked && this.isCheckedState,
            }
        },
        getWithValue(percent) {
            if (this.withHeader) return
            if (this.withValues) return true
            return percent && parseInt(percent) !== 0
        },
        onProgressClick(index, percent, checked) {
            if(this.disabled) return
            if (this.isCheckedState && parseInt(percent) === 0 && !checked) return
            if (!this.isCheckedState && parseInt(percent) === 0) return;
            this.$emit('rangeClick', index);

            const item = this.items[index]; 
            item.checked = !checked;
            this.$set(this.items, index, item);
        },
        triggerShowValue(isVisible) {
            if(isVisible) {
                this.showValue = true;
            }
        },
    }
}
</script>

<style lang="less">
.progress-group {
    .page-padding();

    &_grid {
        font-size: 0;

        @{desktop} &{
            margin-left: -52px;
        }
        .progress-group{
            &__item{
                display: inline-block;
                vertical-align: top;

                @{desktop} &{
                    width: 50%;
                    padding-left: rem(52);
                }
            }
        }
    }

    &_checked{
        .progress-group__title{
            color: @color-gray-light;
        }
    }

    &__header {
        display: flex;
        justify-content: space-between;

        padding-top: rem(4);
        padding-bottom: rem(7);
    }

    &__main {
        display: flex;
        align-items: center;

        padding: rem(2) 0;
    }

    &__caption {
        // color: @color-gray-light;
        color: @color-gray-contrast;
        font-size: rem(11);
        line-height: 1.2;

        padding-bottom: rem(4);

        @{desktop} & {
            padding-top: rem(2.5);
            padding-bottom: rem(5.5);
        }
    }

    &__item {
        margin-bottom: rem(5);

        position: relative;

        transition: color .2s ease;

        &:last-child {
            margin-bottom: 0;
        }

        &_checked {
            font-weight: 500;
        }

        &_unchecked {
            color: @color-gray-light;
        }

        &_with-header {

            @{desktop} & {
                margin-bottom: rem(21);
            }
        }

        &:after {
            content: '';
            display: block;

            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: 1;
        }
    }

    &__item + &__item {

        .progress-group__header {
            padding-top: rem(10);
        }
    }

    &__title {
        flex-shrink: 0;

        font-size: rem(13);
        text-overflow: ellipsis;

        overflow: hidden;
    }

    &__main &__title {
        width: rem(114);
    }

    &__percent {
        font-size: rem(13);
    }

    &__progress {
        flex: 1;

        z-index: 2;
    }

    &_checkable &__item {
        cursor: pointer;
    }

    &_checkable &__item_empty {
        cursor: default;
    }

}
</style>
